import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { motion } from 'framer-motion';
import { colors, Container, Button, Card, Section } from '../styles/GlobalStyles';

const checkmark = keyframes`
  0% {
    stroke-dasharray: 0 50;
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dasharray: 50 50;
    stroke-dashoffset: 0;
  }
`;

const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SuccessSection = styled(Section)`
  background: linear-gradient(135deg, ${colors.cream} 0%, ${colors.white} 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
`;

const SuccessCard = styled(Card)`
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const CheckIcon = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 30px;
  background: ${colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${bounce} 0.6s ease-out;
  
  svg {
    width: 60px;
    height: 60px;
    stroke: white;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
    animation: ${checkmark} 0.6s ease-out 0.3s both;
  }
`;

const SuccessTitle = styled.h1`
  color: ${colors.primary};
  font-size: 2.5rem;
  margin-bottom: 20px;
`;

const SuccessMessage = styled.p`
  color: ${colors.brown};
  font-size: 1.3rem;
  line-height: 1.6;
  margin-bottom: 30px;
`;

const PaymentDetails = styled.div`
  background: ${colors.secondary};
  padding: 25px;
  border-radius: 12px;
  margin: 30px 0;
  border: 2px solid ${colors.primary};
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  padding: 10px 0;
  border-bottom: 1px solid ${colors.cream};
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    font-weight: bold;
    font-size: 1.1rem;
  }
`;

const DetailLabel = styled.span`
  color: ${colors.brown};
  font-weight: 600;
`;

const DetailValue = styled.span`
  color: ${colors.primary};
  font-weight: bold;
`;

const ProcessingIndicator = styled.div<{ isProcessing: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: ${props => props.isProcessing ? colors.cream : '#e8f5e8'};
  border: 2px solid ${props => props.isProcessing ? colors.secondary : '#28a745'};
  padding: 15px;
  border-radius: 8px;
  margin: 20px 0;
  font-weight: 600;
  color: ${props => props.isProcessing ? colors.brown : '#28a745'};
  
  ${props => props.isProcessing && css`
    animation: ${bounce} 1.5s ease-in-out infinite;
  `}
`;

const BackHomeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 15px 30px;
  margin-top: 30px;
  background: linear-gradient(135deg, ${colors.secondary} 0%, #ffed4e 100%);
  border: 2px solid ${colors.primary};
  border-radius: 30px;
  color: ${colors.brown};
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.4s ease;
  box-shadow: 0 4px 15px rgba(139, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
    transition: left 0.6s;
  }
  
  &:hover {
    background: linear-gradient(135deg, #ffed4e 0%, ${colors.secondary} 100%);
    color: ${colors.primary};
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 8px 25px rgba(139, 0, 0, 0.3);
    border-color: ${colors.darkRed};
    
    &:before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(-1px) scale(1.02);
    box-shadow: 0 4px 15px rgba(139, 0, 0, 0.2);
  }
  
  .home-icon {
    font-size: 1.2rem;
    transition: transform 0.3s ease;
  }
  
  &:hover .home-icon {
    transform: scale(1.2) rotate(10deg);
  }
  
  @media (max-width: 768px) {
    padding: 12px 24px;
    margin-top: 25px;
    font-size: 1rem;
    border-radius: 25px;
    gap: 8px;
    
    &:hover {
      transform: none; /* Rimuove l'effetto hover su mobile */
      box-shadow: 0 4px 15px rgba(139, 0, 0, 0.2);
    }
    
    &:hover .home-icon {
      transform: none;
    }
  }
  
  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 0.95rem;
    margin-top: 20px;
  }
`;

interface PaymentSuccessProps {
  paymentDetails: any;
  formData: any;
  isBackendProcessing: boolean;
  onBackClick: () => void;
  submitError?: string;
  submitSuccess?: string;
}

const PaymentSuccess: React.FC<PaymentSuccessProps> = ({ 
  paymentDetails, 
  formData, 
  isBackendProcessing,
  onBackClick,
  submitError,
  submitSuccess 
}) => {
  const bicchieri = formData.menuPartecipanti.filter((p: any) => p.bicchiereCommemorativo).length;

  return (
    <SuccessSection>
      <Container>
        <SuccessCard>
          <CheckIcon>
            <svg viewBox="0 0 50 50">
              <path d="M10 25 L20 35 L40 15" fill="none" />
            </svg>
          </CheckIcon>
          
          <SuccessTitle>🎉 Pagamento Completato!</SuccessTitle>
          
          <SuccessMessage>
            Il tuo pagamento PayPal è stato elaborato con successo.<br />
            Grazie per aver prenotato il tuo posto ad Aria di Fest!
          </SuccessMessage>

          <PaymentDetails>
            <DetailRow>
              <DetailLabel>📧 Email:</DetailLabel>
              <DetailValue>{formData.email}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>👤 Referente:</DetailLabel>
              <DetailValue>{formData.nome} {formData.cognome}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>👥 Partecipanti:</DetailLabel>
              <DetailValue>{formData.numeroPartecipanti}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>💳 ID Transazione:</DetailLabel>
              <DetailValue>{paymentDetails.orderId}</DetailValue>
            </DetailRow>
            {bicchieri > 0 && (
              <DetailRow>
                <DetailLabel>🍺 Bicchieri commemorativi:</DetailLabel>
                <DetailValue>{bicchieri}</DetailValue>
              </DetailRow>
            )}
            <DetailRow>
              <DetailLabel>{paymentDetails.isFullPayment ? '💰 Pagamento Completo:' : '💰 Acconto Pagato:'}</DetailLabel>
              <DetailValue>€{paymentDetails.amount}</DetailValue>
            </DetailRow>
          </PaymentDetails>

          {isBackendProcessing && (
            <ProcessingIndicator isProcessing={true}>
              🔄 Stiamo completando la registrazione e inviando l'email di conferma...
            </ProcessingIndicator>
          )}

          {!isBackendProcessing && submitSuccess && (
            <ProcessingIndicator isProcessing={false}>
              ✅ Registrazione completata! Controlla la tua email per i dettagli.
            </ProcessingIndicator>
          )}

          {!isBackendProcessing && submitError && (
            <ProcessingIndicator isProcessing={false} style={{ 
              background: '#ffe6e6', 
              borderColor: '#ff4444', 
              color: '#cc0000' 
            }}>
              ⚠️ {submitError}
            </ProcessingIndicator>
          )}

          <SuccessMessage style={{ fontSize: '1rem', marginTop: '20px' }}>
            📱 <strong>Cosa succede ora?</strong><br />
            1. Riceverai un'email di conferma con il QR code<br />
            2. Il saldo rimanente sarà pagato il giorno dell'evento<br />
            3. Porta il QR code o stampa l'email per l'ingresso
          </SuccessMessage>

          <BackHomeButton onClick={onBackClick}>
            <span className="home-icon">🏠</span>
            Torna alla Home
          </BackHomeButton>
        </SuccessCard>
      </Container>
    </SuccessSection>
  );
};

export default PaymentSuccess;
